// Trang hiển thị danh sách đơn đã phê duyệt và từ chối (có phân trang)
import React, { useEffect, useState } from 'react';
import { Table, Card, Spin, message, Tag, Button, Modal, Descriptions, Input, Row, Col, Tabs } from 'antd';
import api from '../api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Map trạng thái sang chữ và màu
const statusMap = {
  0: { text: 'Chờ phê duyệt', color: 'gold' },
  1: { text: 'Duyệt', color: 'green' },
  2: { text: 'Từ chối', color: 'red' },
  4: { text: 'Lock đơn phê duyệt', color: 'gray' },
};

function HistoryList() {
  const [activeTab, setActiveTab] = useState('approved'); // 'approved' hoặc 'rejected'
  
  // State cho tab Đơn đã duyệt
  const [approvedData, setApprovedData] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [approvedPagination, setApprovedPagination] = useState({ current: 1, pageSize: 30, total: 0 });
  const [approvedSearchKeyword, setApprovedSearchKeyword] = useState('');
  
  // State cho tab Đơn từ chối
  const [rejectedData, setRejectedData] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedPagination, setRejectedPagination] = useState({ current: 1, pageSize: 30, total: 0 });
  const [rejectedSearchKeyword, setRejectedSearchKeyword] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Hàm fetch dữ liệu cho đơn đã duyệt (status = 1) - chỉ fetch từng page
  const fetchApprovedData = async (page = 1, keyword = approvedSearchKeyword) => {
    setApprovedLoading(true);
    try {
      const res = await api.get('/orders/history', {
        params: { page, pageSize: 100, keyword: keyword?.trim() || undefined },
      });
      const rawData = res.data?.data || [];
      const totalFromAPI = res.data?.total || 0;
      
      // Lọc chỉ lấy đơn đã duyệt (status = 1)
      const filteredData = rawData.filter(item => item.status === 1);
      
      // Xử lý dữ liệu
      const processedData = filteredData.map((item, index) => ({
        ...item,
        stt: (page - 1) * 30 + index + 1,
      }));
      
      setApprovedData(processedData);
      
      // Tính total dựa trên số lượng đã filter (ước tính)
      // Nếu có keyword, total sẽ chính xác hơn
      const estimatedTotal = keyword ? filteredData.length : Math.floor(totalFromAPI * (filteredData.length / rawData.length || 0.5));
      
      setApprovedPagination({
        current: page,
        pageSize: 30,
        total: estimatedTotal > 0 ? estimatedTotal : filteredData.length,
      });
    } catch (err) {
      message.error('Lỗi khi tải dữ liệu đơn đã duyệt!');
    } finally {
      setApprovedLoading(false);
    }
  };

  // Hàm fetch dữ liệu cho đơn từ chối (status = 2) - chỉ fetch từng page
  const fetchRejectedData = async (page = 1, keyword = rejectedSearchKeyword) => {
    setRejectedLoading(true);
    try {
      const res = await api.get('/orders/history', {
        params: { page, pageSize: 100, keyword: keyword?.trim() || undefined },
      });
      const rawData = res.data?.data || [];
      const totalFromAPI = res.data?.total || 0;
      
      // Lọc chỉ lấy đơn từ chối (status = 2)
      const filteredData = rawData.filter(item => item.status === 2);
      
      // Xử lý dữ liệu
      const processedData = filteredData.map((item, index) => ({
        ...item,
        stt: (page - 1) * 30 + index + 1,
      }));
      
      setRejectedData(processedData);
      
      // Tính total dựa trên số lượng đã filter (ước tính)
      const estimatedTotal = keyword ? filteredData.length : Math.floor(totalFromAPI * (filteredData.length / rawData.length || 0.5));
      
      setRejectedPagination({
        current: page,
        pageSize: 30,
        total: estimatedTotal > 0 ? estimatedTotal : filteredData.length,
      });
    } catch (err) {
      message.error('Lỗi khi tải dữ liệu đơn từ chối!');
    } finally {
      setRejectedLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'approved') {
      fetchApprovedData(1, approvedSearchKeyword);
    } else {
      fetchRejectedData(1, rejectedSearchKeyword);
    }
    // eslint-disable-next-line
  }, [approvedSearchKeyword, rejectedSearchKeyword, activeTab]);

  // Hàm xuất Excel cho đơn đã duyệt
  const exportApprovedToExcel = async () => {
    try {
      const hideLoading = message.loading('Đang tải và xuất dữ liệu...', 0);
      let allData = [];
      let page = 1;
      let pageSize = 100;
      let keepFetching = true;
      
      while (keepFetching) {
        const res = await api.get('/orders/history', {
          params: { page, pageSize, keyword: approvedSearchKeyword?.trim() || undefined },
        });
        const pageData = res.data?.data || [];
        const total = res.data?.total || 0;
        
        // Lọc chỉ lấy đơn đã duyệt
        const filteredData = pageData.filter(item => item.status === 1);
        allData = allData.concat(filteredData);
        
        if (pageData.length === 0 || page >= Math.ceil(total / pageSize)) {
          keepFetching = false;
        } else {
          page++;
        }
      }
      
      hideLoading();
      const exportData = allData.map((row, idx) => ({
        STT: idx + 1,
        ID: row.id,
        Tài_khoản: row.account,
        Tên_đầy_đủ: row.full_name,
        'Số_lần_trùng_tên': row.duplicateInfo?.fullname_count || 0,
        Mã_khuyến_mãi: row.promo_code,
        Tiền_nạp: row.deposit_amount,
        Tiền_thưởng: row.bonus_amount,
        Đại_lý: row.franchisee,
        VIP: row.vip_level,
        Ngân_hàng: row.bank_name,
        'Số_lần_trùng_ngân_hàng': row.duplicateInfo?.bank_name_count || 0,
        Số_TK: row.bank_account_number,
        IP: row.device_ip,
        'Số_lần_trùng_IP': row.duplicateInfo?.device_ip_count || 0,
        FP: row.device_id,
        'Số_lần_trùng_FP': row.duplicateInfo?.device_id_count || 0,
        'Trùng_toàn_bộ': row.duplicateInfo?.combined_count || 0,
        Trạng_thái: statusMap[row.status]?.text || row.status,
        Người_duyệt: row.user_approve,
        Thời_gian_duyệt: row.time_approve,
        Ngày_tạo: row.createdAt,
        Ghi_chú: row.memo,
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Đơn đã duyệt');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const fileName = `don_da_duyet_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), fileName);
      message.success('Xuất Excel đơn đã duyệt thành công!');
    } catch (err) {
      message.error('Lỗi khi xuất Excel!');
    }
  };

  // Hàm xuất Excel cho đơn từ chối
  const exportRejectedToExcel = async () => {
    try {
      const hideLoading = message.loading('Đang tải và xuất dữ liệu...', 0);
      let allData = [];
      let page = 1;
      let pageSize = 100;
      let keepFetching = true;
      
      while (keepFetching) {
        const res = await api.get('/orders/history', {
          params: { page, pageSize, keyword: rejectedSearchKeyword?.trim() || undefined },
        });
        const pageData = res.data?.data || [];
        const total = res.data?.total || 0;
        
        // Lọc chỉ lấy đơn từ chối
        const filteredData = pageData.filter(item => item.status === 2);
        allData = allData.concat(filteredData);
        
        if (pageData.length === 0 || page >= Math.ceil(total / pageSize)) {
          keepFetching = false;
        } else {
          page++;
        }
      }
      
      hideLoading();
      const exportData = allData.map((row, idx) => ({
        STT: idx + 1,
        ID: row.id,
        Tài_khoản: row.account,
        Tên_đầy_đủ: row.full_name,
        'Số_lần_trùng_tên': row.duplicateInfo?.fullname_count || 0,
        Mã_khuyến_mãi: row.promo_code,
        Tiền_nạp: row.deposit_amount,
        Tiền_thưởng: row.bonus_amount,
        Đại_lý: row.franchisee,
        VIP: row.vip_level,
        Ngân_hàng: row.bank_name,
        'Số_lần_trùng_ngân_hàng': row.duplicateInfo?.bank_name_count || 0,
        Số_TK: row.bank_account_number,
        IP: row.device_ip,
        'Số_lần_trùng_IP': row.duplicateInfo?.device_ip_count || 0,
        FP: row.device_id,
        'Số_lần_trùng_FP': row.duplicateInfo?.device_id_count || 0,
        'Trùng_toàn_bộ': row.duplicateInfo?.combined_count || 0,
        Trạng_thái: statusMap[row.status]?.text || row.status,
        Người_duyệt: row.user_approve,
        Thời_gian_duyệt: row.time_approve,
        Ngày_tạo: row.createdAt,
        Ghi_chú: row.memo,
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Đơn từ chối');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const fileName = `don_tu_choi_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), fileName);
      message.success('Xuất Excel đơn từ chối thành công!');
    } catch (err) {
      message.error('Lỗi khi xuất Excel!');
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    { title: 'STT', dataIndex: 'stt', key: 'stt', width: 60 },
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Tài khoản', dataIndex: 'account', key: 'account' },
    { 
      title: 'Tên đầy đủ', 
      dataIndex: 'full_name', 
      key: 'full_name',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.fullname_count > 1 && (
            <Tag color="blue" size="small">{record.duplicateInfo.fullname_count}</Tag>
          )}
        </div>
      )
    },
    { title: 'Mã khuyến mãi', dataIndex: 'promo_code', key: 'promo_code' },
    { title: 'Tiền nạp', dataIndex: 'deposit_amount', key: 'deposit_amount' },
    { title: 'Tiền thưởng', dataIndex: 'bonus_amount', key: 'bonus_amount' },
    { title: 'Đại lý', dataIndex: 'franchisee', key: 'franchisee' },
    { title: 'VIP', dataIndex: 'vip_level', key: 'vip_level', width: 70 },
    { 
      title: 'Ngân hàng', 
      dataIndex: 'bank_name', 
      key: 'bank_name',
    },
    { title: 'Số TK', dataIndex: 'bank_account_number', key: 'bank_account_number',
    render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.bank_number_count > 1 && (
            <Tag color="blue" size="small">{record.duplicateInfo.bank_number_count}</Tag>
          )}
        </div>
      )
    },
    { 
      title: 'IP', 
      dataIndex: 'device_ip', 
      key: 'device_ip',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.device_ip_count > 1 && (
            <Tag color="blue" size="small">{record.duplicateInfo.device_ip_count}</Tag>
          )}
        </div>
      )
    },
    { 
      title: 'FP', 
      dataIndex: 'device_id', 
      key: 'device_id',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.device_id_count > 1 && (
            <Tag color="blue" size="small">{record.duplicateInfo.device_id_count}</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Trùng toàn bộ',
      key: 'combined_duplicate',
      width: 120,
      render: (_, record) => (
        record.duplicateInfo?.combined_count > 1 ? (
          <Tag color="blue" size="small">{record.duplicateInfo.combined_count}</Tag>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const s = statusMap[status] || { text: 'Không xác định', color: 'default' };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    { title: 'Người duyệt', dataIndex: 'user_approve', key: 'user_approve' },
    { title: 'Thời gian duyệt', dataIndex: 'time_approve', key: 'time_approve' },
    { title: 'Ngày tạo', dataIndex: 'createdAt', key: 'createdAt' },
    { title: 'Ghi chú', dataIndex: 'memo', key: 'memo' },
    {
      title: 'Chức năng',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Button type="link" onClick={() => { setSelectedRow(record); setModalOpen(true); }}>
          Xem chi tiết
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'approved',
      label: `Đơn đã duyệt (${approvedPagination.total})`,
      children: (
        <>
          <Row gutter={[8, 16]} style={{ marginBottom: 16 }}>
            <Col span={16}>
              <Input
                placeholder="Tìm kiếm tài khoản, device_ip, device_id..."
                value={approvedSearchKeyword}
                onChange={e => setApprovedSearchKeyword(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={8}>
              <Button type="primary" onClick={exportApprovedToExcel} style={{ marginBottom: 0 }}>
                Xuất Excel
              </Button>
            </Col>
          </Row>
          <Spin spinning={approvedLoading}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={approvedData}
              pagination={{
                current: approvedPagination.current,
                pageSize: approvedPagination.pageSize,
                total: approvedPagination.total,
                onChange: (page) => fetchApprovedData(page, approvedSearchKeyword),
              }}
              scroll={{ x: 1800 }}
            />
          </Spin>
        </>
      ),
    },
    {
      key: 'rejected',
      label: `Đơn từ chối (${rejectedPagination.total})`,
      children: (
        <>
          <Row gutter={[8, 16]} style={{ marginBottom: 16 }}>
            <Col span={16}>
              <Input
                placeholder="Tìm kiếm tài khoản, device_ip, device_id..."
                value={rejectedSearchKeyword}
                onChange={e => setRejectedSearchKeyword(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={8}>
              <Button type="primary" danger onClick={exportRejectedToExcel} style={{ marginBottom: 0 }}>
                Xuất Excel
              </Button>
            </Col>
          </Row>
          <Spin spinning={rejectedLoading}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={rejectedData}
              pagination={{
                current: rejectedPagination.current,
                pageSize: rejectedPagination.pageSize,
                total: rejectedPagination.total,
                onChange: (page) => fetchRejectedData(page, rejectedSearchKeyword),
              }}
              scroll={{ x: 1800 }}
            />
          </Spin>
        </>
      ),
    },
  ];

  return (
    <Card title="Lịch sử đơn đã phê duyệt">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
      <Modal
        open={modalOpen}
        title="Chi tiết đơn"
        onCancel={() => setModalOpen(false)}
        footer={null}
      >
        {selectedRow && (
          <Descriptions column={1} bordered size="small">
            {Object.entries(selectedRow).map(([key, value]) => (
              <Descriptions.Item label={key} key={key}>
                {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
}

export default HistoryList; 